import Button from "@/components/common/Button";
import { XMarkIcon, CloudArrowUpIcon, DocumentIcon, EnvelopeIcon, PlusIcon } from "@heroicons/react/24/outline";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useInviteEmployeeModal } from "@/hooks/useInviteEmployeeModal";
import { useCreateDepartment } from "@/hooks/queries/useDepartmentMutations";

interface InviteEmployeeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onInvite: (emails: string[], departmentId?: string) => void;
  isSubmitting: boolean;
  departmentName?: string;
  isDepartmentAdmin?: boolean;
  departments?: Array<{ _id: string; name: string }>;
  companyId?: string;
}

const InviteEmployeeModal = ({
  isOpen,
  onClose,
  onInvite,
  isSubmitting,
  departmentName,
  isDepartmentAdmin = false,
  departments = [],
  companyId,
}: InviteEmployeeModalProps) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Department selection state for company admins
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<string>("");
  const [showCreateDepartment, setShowCreateDepartment] = useState(false);
  const [newDepartmentName, setNewDepartmentName] = useState("");
  const [isCreatingDepartment, setIsCreatingDepartment] = useState(false);
  
  // Create department mutation
  const createDepartmentMutation = useCreateDepartment();
  
  const {
    emails,
    csvEmails,
    totalEmails,
    handleCsvUpload,
    handleEmailChange,
    handleSubmit,
    handleClose,
  } = useInviteEmployeeModal({ 
    onInvite: (emails: string[]) => {
      // For company admins, pass the selected department ID
      if (!isDepartmentAdmin && selectedDepartmentId) {
        onInvite(emails, selectedDepartmentId);
      } else {
        onInvite(emails);
      }
    }, 
    onClose 
  });

  // Handle creating a new department
  const handleCreateDepartment = async () => {
    if (!newDepartmentName.trim() || !companyId) return;
    
    try {
      setIsCreatingDepartment(true);
      const newDepartment = await createDepartmentMutation.mutateAsync({
        name: newDepartmentName.trim(),
        company: companyId,
      });
      
      // Set the newly created department as selected
      setSelectedDepartmentId(newDepartment._id);
      setShowCreateDepartment(false);
      setNewDepartmentName("");
    } catch (error) {
      console.error("Error creating department:", error);
    } finally {
      setIsCreatingDepartment(false);
    }
  };



  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-end justify-center px-2 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={handleClose} />
        
        <span className="hidden sm:inline-block sm:h-screen sm:align-middle" aria-hidden="true">
          &#8203;
        </span>
        
        <div className="inline-block transform overflow-hidden rounded-lg bg-white px-4 pt-6 pb-6 text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl sm:p-6 sm:align-middle dark:bg-gray-800">
          <div className="absolute right-0 top-0 pr-4 pt-6 sm:pr-6">
            <button
              type="button"
              className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-800 dark:text-gray-300 dark:hover:text-gray-200"
              onClick={handleClose}
            >
              <span className="sr-only">{t("common.close", "Close")}</span>
              <XMarkIcon className="h-5 w-5 sm:h-6 sm:w-6" />
            </button>
          </div>
          
          <div className="sm:flex sm:items-start">
            <div className="mt-3 w-full text-center sm:mt-0 sm:text-left">
              {/* Header */}
              <div className="flex items-center space-x-3 mb-4">
                <div className="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 bg-primary-100 dark:bg-primary-900/20 rounded-lg flex-shrink-0">
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                  </svg>
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="text-lg sm:text-xl font-semibold leading-6 text-gray-900 dark:text-white break-words">
                    {t("admin.inviteEmployees", "Invite Employees")}
                  </h3>
                  <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-1 break-words">
                    {isDepartmentAdmin 
                      ? t("admin.inviteDepartmentDescription", "Send invitations to new team members for your department.")
                      : t("admin.inviteCompanyDescription", "Send invitations to new team members for your company.")
                    }
                  </p>
                </div>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
                {/* Department Section */}
                {isDepartmentAdmin && departmentName && (
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t("admin.department", "Department")}
                    </label>
                    <div className="flex items-center space-x-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg">
                      <div className="flex items-center justify-center w-5 h-5 sm:w-6 sm:h-6 bg-blue-500 rounded-full flex-shrink-0">
                        <svg className="w-3 h-3 sm:w-4 sm:h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <span className="text-xs sm:text-sm font-medium text-blue-900 dark:text-blue-100 break-words">{departmentName}</span>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 break-words">
                      {t("admin.departmentRestriction", "You can only invite employees to your department.")}
                    </p>
                  </div>
                )}

                {/* Company Admin Department Selection */}
                {!isDepartmentAdmin && (
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t("admin.selectDepartment", "Select Department")}
                    </label>
                    
                    {departments.length > 0 ? (
                      <div className="space-y-3">
                        <select
                          value={selectedDepartmentId}
                          onChange={(e) => setSelectedDepartmentId(e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                          required
                        >
                          <option value="">{t("admin.chooseDepartment", "Choose a department...")}</option>
                          {departments.map((dept) => (
                            <option key={dept._id} value={dept._id}>
                              {dept.name}
                            </option>
                          ))}
                        </select>
                        
                        <button
                          type="button"
                          onClick={() => setShowCreateDepartment(true)}
                          className="flex items-center space-x-2 text-xs sm:text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
                        >
                          <PlusIcon className="w-3 h-3 sm:w-4 sm:h-4" />
                          <span className="break-words">{t("admin.createNewDepartment", "Create new department")}</span>
                        </button>
                      </div>
                    ) : (
                      <div className="text-center py-4">
                        <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mb-3 break-words">
                          {t("admin.noDepartments", "No departments found. Create one to invite employees.")}
                        </p>
                        <button
                          type="button"
                          onClick={() => setShowCreateDepartment(true)}
                          className="inline-flex items-center space-x-2 px-3 sm:px-4 py-2 text-xs sm:text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        >
                          <PlusIcon className="w-3 h-3 sm:w-4 sm:h-4" />
                          <span>{t("admin.createDepartment", "Create Department")}</span>
                        </button>
                      </div>
                    )}

                    {/* Create Department Form */}
                    {showCreateDepartment && (
                      <div className="mt-4 p-3 sm:p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
                        <h4 className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white mb-2">
                          {t("admin.createNewDepartment", "Create New Department")}
                        </h4>
                        <div className="flex flex-col sm:flex-row gap-2">
                          <input
                            type="text"
                            value={newDepartmentName}
                            onChange={(e) => setNewDepartmentName(e.target.value)}
                            placeholder={t("admin.departmentNamePlaceholder", "Department name...")}
                            className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-800 dark:text-white"
                          />
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={handleCreateDepartment}
                              disabled={!newDepartmentName.trim() || isCreatingDepartment}
                              className="px-3 sm:px-4 py-2 text-xs sm:text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {isCreatingDepartment ? t("common.creating", "Creating...") : t("common.create", "Create")}
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setShowCreateDepartment(false);
                                setNewDepartmentName("");
                              }}
                              className="px-3 sm:px-4 py-2 text-xs sm:text-sm bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-md hover:bg-gray-400 dark:hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-500"
                            >
                              {t("common.cancel", "Cancel")}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Employee Emails Section */}
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    {t("admin.employeeEmails", "Employee Emails")}
                  </label>
                  
                  {/* CSV Upload Area */}
                  <div className="mb-4">
                    <div className="relative">
                      <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4 sm:p-8 text-center hover:border-primary-400 dark:hover:border-primary-500 transition-colors duration-200">
                        <CloudArrowUpIcon className="mx-auto h-8 w-8 sm:h-12 sm:w-12 text-gray-400 dark:text-gray-500" />
                        <div className="mt-2 sm:mt-4">
                          <p className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white mb-1 break-words">
                            {csvEmails.length > 0
                              ? `${csvEmails.length} emails loaded`
                              : t("admin.uploadCsvPrompt", "Upload CSV file with employee emails")}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 break-words">
                            {csvEmails.length > 0
                              ? t("admin.fileProcessed", "File successfully processed")
                              : t("admin.dragDropPrompt", "Drag and drop or click to browse")}
                          </p>
                        </div>
                        
                        <input
                          type="file"
                          accept=".csv"
                          ref={fileInputRef}
                          onChange={handleCsvUpload}
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        />
                        
                        <div className="mt-2 sm:mt-4">
                          <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            className={`px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium rounded-md transition-colors duration-200 flex items-center space-x-2 mx-auto ${
                              csvEmails.length === 0
                                ? "bg-primary-600 hover:bg-primary-700 text-white"
                                : "bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600"
                            }`}
                          >
                            <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                            </svg>
                            <span className="break-words">
                              {csvEmails.length === 0
                                ? t("admin.chooseFile", "Choose File")
                                : t("admin.changeFile", "Change File")}
                            </span>
                          </button>
                        </div>
                      </div>
                    </div>
                    
                    {/* CSV Template Download */}
                    <div className="mt-3">
                      <a
                        href="/employee-emails-template.csv"
                        download="employee-emails-template.csv"
                        className="inline-flex items-center space-x-2 px-3 sm:px-4 py-2 text-xs sm:text-sm bg-primary-50 hover:bg-primary-100 dark:bg-primary-900/20 dark:hover:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-lg transition-all duration-200 font-medium border border-primary-200 dark:border-primary-700 hover:border-primary-300 dark:hover:border-primary-600"
                      >
                        <DocumentIcon className="w-3 h-3 sm:w-4 sm:h-4" />
                        <EnvelopeIcon className="w-3 h-3 sm:w-4 sm:h-4" />
                        <span className="break-words">📥 {t("admin.downloadCsvTemplate", "Download CSV Template")}</span>
                      </a>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 break-words">
                        {t("admin.csvFormat", "Format: one email per line or comma-separated")}
                      </p>
                    </div>
                  </div>
                  
                  {/* Manual Email Entry */}
                  <div>
                    <textarea
                      rows={4}
                      value={emails}
                      onChange={(e) => handleEmailChange(e.target.value)}
                      placeholder={t("admin.emailsPlaceholder", "Enter emails separated by comma, space, or new line, or upload a CSV file above")}
                      className="block w-full text-sm rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                </div>

                {/* Invitation Confirmation */}
                {totalEmails > 0 && (
                  <div className="flex items-start space-x-2 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <svg className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500 dark:text-blue-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    <span className="text-xs sm:text-sm text-blue-900 dark:text-blue-100 break-words">
                      {t("admin.invitingTo", "You're inviting employees to:")} 
                      <span className="font-medium text-blue-700 dark:text-blue-300 ml-1">
                        {isDepartmentAdmin ? departmentName : t("admin.company", "Company")}
                      </span>
                    </span>
                  </div>
                )}
                
                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleClose}
                    disabled={isSubmitting}
                    className="w-full sm:w-auto text-xs sm:text-sm"
                  >
                    {t("common.cancel", "Cancel")}
                  </Button>
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={isSubmitting || totalEmails === 0}
                    className="flex items-center space-x-2 w-full sm:w-auto text-xs sm:text-sm"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="h-3 w-3 sm:h-4 sm:w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                        <span className="break-words">{t("admin.inviting", "Inviting...")}</span>
                      </>
                    ) : (
                      <>
                        <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                        <span className="break-words">{t("admin.sendInvites", "Send Invites")}</span>
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InviteEmployeeModal;
